import { useState } from "react"
import { Link } from "@inertiajs/react"
import { FiArrowLeft, FiBarChart2, FiCheck, FiEdit3, FiExternalLink, FiEye, FiMail, FiMessageCircle, FiMoreHorizontal, FiPlus, FiShare2, FiTrendingUp, FiUsers } from "react-icons/fi"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { CardShareModal } from "../../components/CardShareModal"
import { MiniBarChart } from "../../components/MiniBarChart"

type Card = {
  title: string
  slug: string
  is_published: boolean
  public_url: string
  vcf_url: string
  edit_url: string
  analytics_url: string
  preview_url: string
  share_url: string
  details_url: string
  contact_url: string
  design_url: string
  inquiries_url: string
  unread_inquiries: number
  setup: {
    details: boolean
    contact: boolean
    design: boolean
    published: boolean
  }
  analytics: {
    visits: number
    unique_visitors: number
    action_clicks: number
    actions: Record<string, number>
    series: Array<{ label: string; value: number }>
  }
}

type Props = {
  card: Card | null
  new_card_path: string
}

export default function CustomerDashboard({ card, new_card_path }: Props) {
  const [actionsOpen, setActionsOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  return (
    <AppLayout title="הכרטיס שלי" customerMode>
      <div className="mb-5">
        <h1 className="text-2xl font-bold sm:text-3xl">הכרטיס שלי</h1>
        <p className="mt-1 text-sm text-slate-600 sm:text-base">כל מה שצריך לניהול הכרטיס העסקי שלך</p>
      </div>

      {!card ? (
        <div className="glass-card mx-auto max-w-2xl border-brand-100 p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-brand-50 text-brand-700"><FiPlus size={26} aria-hidden="true" /></div>
          <h2 className="mt-5 text-2xl font-semibold">בואו ניצור את הכרטיס הראשון שלך</h2>
          <p className="mt-2 text-slate-600">נוסיף כמה פרטים בסיסיים, ואפשר יהיה להשלים את העיצוב אחר כך.</p>
          <Link href={new_card_path} className="btn-primary mt-6 gap-2 bg-brand-600"><FiPlus aria-hidden="true" />יצירת כרטיס</Link>
        </div>
      ) : (
        <div className="space-y-4">
          <section className="glass-card relative z-20 overflow-visible p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm text-slate-500"><span className="h-2 w-2 rounded-full bg-emerald-500" />/{card.slug}</div>
                <h2 className="text-xl font-semibold sm:text-2xl">{card.title}</h2>
              </div>
              <Badge tone={card.is_published ? "green" : "amber"}>{card.is_published ? "פורסם" : "טיוטה"}</Badge>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Link href={card.edit_url} className="btn-primary min-w-0 flex-1 flex-nowrap gap-2 whitespace-nowrap bg-brand-600 text-center sm:flex-none"><FiEdit3 className="shrink-0" aria-hidden="true" />עריכת הכרטיס</Link>
              <div className="relative">
                <button
                  type="button"
                  className="btn-secondary min-h-11 px-4"
                  aria-haspopup="menu"
                  aria-expanded={actionsOpen}
                  onClick={() => setActionsOpen((open) => !open)}
                >
                  <FiMoreHorizontal aria-hidden="true" /> <span className="sr-only">עוד</span>
                </button>
                {actionsOpen ? (
                  <div className="absolute end-0 z-50 mt-2 min-w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" role="menu">
                    <Link href={card.analytics_url} role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" onClick={() => setActionsOpen(false)}>
                      <FiBarChart2 aria-hidden="true" />ביצועים
                    </Link>
                    <Link href={card.preview_url} role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" onClick={() => setActionsOpen(false)}>
                      <FiEye aria-hidden="true" />תצוגה
                    </Link>
                    <Link href={card.inquiries_url} role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" onClick={() => setActionsOpen(false)}>
                      <FiMessageCircle aria-hidden="true" />פניות{card.unread_inquiries > 0 ? <span className="ms-auto rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700">{card.unread_inquiries}</span> : null}
                    </Link>
                    <button type="button" role="menuitem" className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" onClick={() => { setActionsOpen(false); setShareOpen(true) }}>
                      <FiShare2 aria-hidden="true" />שיתוף הכרטיס
                    </button>
                    <Link href={card.share_url} role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" onClick={() => setActionsOpen(false)}>
                      <FiMail aria-hidden="true" />חתימת אימייל
                    </Link>
                    <a href={card.public_url} role="menuitem" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm hover:bg-slate-50" target="_blank" rel="noreferrer" onClick={() => setActionsOpen(false)}>
                      <FiExternalLink aria-hidden="true" />פתיחת הכרטיס
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <SetupChecklist card={card} />

          <section className="glass-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><FiTrendingUp className="text-brand-600" aria-hidden="true" />איך הכרטיס שלך עובד</h2>
              <span className="text-xs text-slate-500">30 ימים אחרונים</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500"><FiBarChart2 aria-hidden="true" />כניסות</div>
                <div className="mt-1 text-2xl font-semibold">{card.analytics.visits}</div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500"><FiUsers aria-hidden="true" />אנשים שונים</div>
                <div className="mt-1 text-2xl font-semibold">{card.analytics.unique_visitors}</div>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500"><FiShare2 aria-hidden="true" />פעולות</div>
                <div className="mt-1 text-2xl font-semibold">{card.analytics.action_clicks}</div>
              </div>
            </div>
            <div className="mt-5"><MiniBarChart points={card.analytics.series} /></div>
          </section>
          {shareOpen ? <CardShareModal title={card.title} url={card.public_url} vcfUrl={card.vcf_url} onClose={() => setShareOpen(false)} /> : null}
        </div>
      )}
    </AppLayout>
  )
}

function SetupChecklist({ card }: { card: Card }) {
  const steps = [
    { key: "details", label: "הוספת תיאור קצר", href: card.details_url },
    { key: "contact", label: "הוספת פרטי קשר", href: card.contact_url },
    { key: "design", label: "בחירת מראה", href: card.design_url },
    { key: "published", label: "הצגת הכרטיס לכולם", href: card.edit_url },
  ] as const
  const completed = steps.filter((step) => card.setup[step.key]).length
  const nextStep = steps.find((step) => !card.setup[step.key])

  return (
    <section className="glass-card p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-brand-700">התחלה מהירה</p>
          <h2 className="mt-1 text-xl font-semibold">בואו נשלים את הכרטיס</h2>
          <p className="mt-1 text-sm text-slate-600">כמה צעדים קטנים, והכרטיס מוכן לשיתוף.</p>
        </div>
        <span className="shrink-0 text-sm font-semibold text-slate-500">{completed}/{steps.length}</span>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100" aria-label={`${completed} מתוך ${steps.length} הושלמו`}>
        <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${(completed / steps.length) * 100}%` }} />
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {steps.map((step) => {
          const done = card.setup[step.key]
          return (
            <Link
              key={step.key}
              href={step.href}
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${done ? "border-emerald-100 bg-emerald-50/60 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-brand-200 hover:bg-brand-50/40"}`}
            >
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                {done ? <FiCheck aria-hidden="true" /> : <span className="h-2 w-2 rounded-full bg-current" />}
              </span>
              <span className="flex-1">{step.label}</span>
              {!done ? <FiArrowLeft className="text-slate-400" aria-hidden="true" /> : null}
            </Link>
          )
        })}
      </div>

      {nextStep ? (
        <Link href={nextStep.href} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800">
          {nextStep.label}<FiArrowLeft aria-hidden="true" />
        </Link>
      ) : null}
    </section>
  )
}
